import { useState, useMemo } from "react";
import { ChevronRight, ChevronDown, Building2, Plus } from "lucide-react";
import { useConnectStore } from "@/dentlab/src/store/Connect";
import { VendorSidebarItem } from "../VendorSidebarItem";
import { getLocalStorage, setLocalStorage } from "@/lib/utils/local-storage";
import { AddVendorDialog } from "@/dentlab/src/components/add-vendor-dialog/add-vendor-dialog.component";
import { useUploadGuard } from "@/dentlab/src/hooks/useUploadGuard";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { useRealtimeStore } from "@/lib/store/Realtime";
import { UnreadIndicator } from "@/dentlab/src/components/chat/unread-indicator.component";

interface AppBarVendorsListProps {
	expanded: boolean;
	maxItems?: number;
	onVendorInteraction?: () => void;
	onCollapseChange?: (collapsed: boolean) => void;
	onOverflowClick?: () => void;
}

const STORAGE_KEY = "dentlab-sidebar-vendors-collapsed";

export const AppBarVendorsList: React.FC<AppBarVendorsListProps> = ({
	expanded,
	maxItems,
	onVendorInteraction,
	onCollapseChange,
	onOverflowClick,
}) => {
	const [isCollapsed, setIsCollapsed] = useState(() =>
		getLocalStorage(STORAGE_KEY, false)
	);
	const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
	const visibleCount = maxItems ?? Infinity;

	const checkNavigationAllowed = useUploadGuard();
	const organization = useCentralStore((state) => state.organization);
	const myOrgId = organization?.id ?? "";

	const { connectedVendors, brandsByOrgId } = useConnectStore((state) => ({
		connectedVendors: state.connectedVendors,
		brandsByOrgId: state.brandsByOrgId,
	}));

	const unreadCounts = useRealtimeStore((state) =>
		state.getUnreadCountsByVendorOrgId(myOrgId)
	);

	const vendors = useMemo(() => {
		return connectedVendors
			.map((orgId) => ({
				orgId,
				name: brandsByOrgId[orgId]?.name ?? "Unbekannt",
			}))
			.sort((a, b) => a.name.localeCompare(b.name));
	}, [connectedVendors, brandsByOrgId]);

	const hasVendors = vendors.length > 0;
	const showEmptyState = expanded && !isCollapsed && !hasVendors;

	const totalVendors = vendors.length;
	const needsOverflow = !isCollapsed && totalVendors > visibleCount;
	const displayCount = needsOverflow ? visibleCount - 1 : totalVendors;
	const visibleVendors = isCollapsed ? [] : vendors.slice(0, displayCount);
	const hiddenCount = totalVendors - displayCount;

	const hiddenUnreadSum = useMemo(() => {
		if (!needsOverflow) return 0;
		const visibleIds = new Set(visibleVendors.map((v) => v.orgId));
		let sum = 0;
		for (const [id, count] of Object.entries(unreadCounts)) {
			if (!visibleIds.has(id)) sum += count;
		}
		return sum;
	}, [needsOverflow, visibleVendors, unreadCounts]);

	const handleToggleCollapse = () => {
		const newValue = !isCollapsed;
		setIsCollapsed(newValue);
		setLocalStorage(STORAGE_KEY, newValue);
		onCollapseChange?.(newValue);
	};

	const handleAddClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		setIsAddDialogOpen(true);
	};

	const handleOverflowClick = () => {
		onOverflowClick?.();
	};

	return (
		<div
			className="flex flex-col w-full overflow-hidden"
			data-testid="app-bar-vendors-list"
		>
			{expanded ? (
				<button
					onClick={handleToggleCollapse}
					className="flex-shrink-0 flex items-center w-full px-2 py-1 text-sm font-medium text-gray-700 hover:bg-stone-200 rounded-md transition-colors"
					aria-expanded={!isCollapsed}
					aria-label="Fremdlabore ein-/ausklappen"
					data-testid="vendors-list-toggle"
				>
					{isCollapsed ? (
						<ChevronRight size={20} className="flex-shrink-0" />
					) : (
						<ChevronDown size={20} className="flex-shrink-0" />
					)}
					<span className="ml-1 flex-1 text-left">Fremdlabore</span>
					<div
						onClick={handleAddClick}
						className="flex-shrink-0 p-0.5 rounded hover:bg-stone-300 transition-colors"
						aria-label="Neues Fremdlabor hinzufügen"
						data-testid="vendor-sidebar-add-button"
					>
						<Plus size={18} />
					</div>
				</button>
			) : (
				<div
					onClick={handleToggleCollapse}
					className="relative flex justify-center py-1 rounded-md cursor-pointer transition-colors duration-200 text-gray-700 hover:bg-stone-200"
					data-testid="vendors-list-toggle"
					aria-label="Fremdlabore ein-/ausklappen"
				>
					<Building2 size={18} />
					{hiddenUnreadSum > 0 && (
						<UnreadIndicator
							count={hiddenUnreadSum}
							showCount={false}
							className="absolute top-0.5 right-0.5"
						/>
					)}
				</div>
			)}

			{showEmptyState && (
				<div className="mt-1 px-2 pb-2">
					<button
						onClick={handleAddClick}
						className="group w-full rounded-lg border border-dashed border-stone-300 bg-white/70 hover:bg-white shadow-sm transition-colors flex items-center gap-3 px-3 py-3 text-left"
						aria-label="Fremdlabor hinzufügen"
						data-testid="vendor-empty-state-add-button"
						type="button"
					>
						<div className="w-9 h-9 rounded-full bg-stone-200 flex items-center justify-center group-hover:bg-stone-300 transition-colors flex-shrink-0 overflow-hidden ring-1 ring-stone-300/60">
							<Plus size={18} className="text-gray-700" />
						</div>
						<div className="min-w-0">
							<div className="text-sm font-semibold text-gray-800">
								Fremdlabor hinzufügen
							</div>
							<div className="text-xs text-gray-500">
								Verbinde ein Labor für Fremdarbeiten.
							</div>
						</div>
					</button>
				</div>
			)}

			{!isCollapsed && hasVendors && (
				<div className="flex flex-col gap-0.5 overflow-hidden">
					{visibleVendors.map((vendor) => (
						<VendorSidebarItem
							key={vendor.orgId}
							vendorOrgId={vendor.orgId}
							vendorName={vendor.name}
							expanded={expanded}
							onVendorInteraction={onVendorInteraction}
							onBeforeNavigate={checkNavigationAllowed}
							unreadCount={unreadCounts[vendor.orgId] || 0}
						/>
					))}
					{needsOverflow && expanded && (
						<button
							onClick={handleOverflowClick}
							className="mt-auto flex items-center px-2 py-1 text-sm text-gray-500 hover:text-gray-700 transition-colors rounded-md hover:bg-stone-200 flex-shrink-0"
							data-testid="vendors-overflow-button"
							aria-label={`${hiddenCount} weitere Fremdlabore anzeigen`}
						>
							<span className="flex-1 text-left">
								+{hiddenCount} weitere
							</span>
							<UnreadIndicator
								count={hiddenUnreadSum}
								showCount={true}
								className="flex-shrink-0"
							/>
						</button>
					)}
				</div>
			)}

			{organization && (
				<AddVendorDialog
					open={isAddDialogOpen}
					onClose={() => setIsAddDialogOpen(false)}
					organization={{
						id: organization.id ?? "",
						name: organization.name ?? "",
					}}
				/>
			)}
		</div>
	);
};
